import React from 'react';
import { Lightbulb, Droplets, Clock, Apple, Users, Car, Home as HomeIcon, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Tips = () => {
  const tips = [
    {
      icon: <Clock className="h-8 w-8" />,
      title: 'Pace Yourself',
      description: 'Your body processes about one standard drink per hour. Spread your drinks out over time.',
      details: 'Alternate alcoholic drinks with water or soft drinks. This helps you stay hydrated and slows your alcohol consumption.',
    },
    {
      icon: <Droplets className="h-8 w-8" />,
      title: 'Stay Hydrated',
      description: 'Drink water before, during, and after consuming alcohol.',
      details: 'Alcohol is a diuretic, meaning it makes you lose more fluid. Drinking water helps prevent dehydration and reduces hangover symptoms.',
    },
    {
      icon: <Apple className="h-8 w-8" />,
      title: 'Eat Before and While Drinking',
      description: 'Food slows the absorption of alcohol into your bloodstream.',
      details: 'Eating a proper meal before drinking and snacking while drinking helps your body process alcohol more slowly.',
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: 'Know Your Limits',
      description: 'Understand how alcohol affects you personally and stick to safe limits.',
      details: 'Everyone processes alcohol differently. Pay attention to how you feel and stop before you lose control.',
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Choose Lower-Alcohol Options',
      description: 'Opt for drinks with lower alcohol content or smaller serves.',
      details: 'Consider light beers, wine spritzers, or diluted drinks. These allow you to enjoy social drinking while consuming less alcohol.',
    },
    {
      icon: <Car className="h-8 w-8" />,
      title: 'Plan Your Transport',
      description: 'Never drink and drive. Organize your ride home before you start drinking.',
      details: 'Use public transport, taxis, rideshare services, or designate a sober driver before you go out.',
    },
    {
      icon: <HomeIcon className="h-8 w-8" />,
      title: 'Have Alcohol-Free Days',
      description: 'Take at least two days off from drinking each week.',
      details: 'Regular breaks from alcohol give your body time to recover and help prevent dependence.',
    },
    {
      icon: <MessageCircle className="h-8 w-8" />,
      title: 'Be Social, Not Just Sociable',
      description: 'Focus on the company and conversation, not just the drinks.',
      details: 'Remember that socializing is about connecting with people. You don\'t need alcohol to have a good time.',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1527525443983-6e60c75fff46?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/80 to-cyan-700/80" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Lightbulb className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Tips for Drinking Better</h1>
          <p className="text-xl">Practical advice to help you drink more responsibly</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="text-center mb-12">
          <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Making better choices about drinking doesn't mean you can't enjoy yourself. These practical tips will help you drink more safely and responsibly while still having a great time.
          </p>
        </div>

        {/* Tips Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {tips.map((tip, index) => (
            <Card 
              key={index} 
              className="hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group"
            >
              <CardHeader className="bg-gradient-to-br from-teal-500 to-cyan-500 text-white">
                <CardTitle className="flex items-center text-xl">
                  <div className="mr-3 group-hover:scale-110 transition-transform duration-300">
                    {tip.icon}
                  </div>
                  {tip.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-900 font-semibold mb-3">{tip.description}</p>
                <p className="text-gray-600 leading-relaxed">{tip.details}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Advice Section */}
        <Card className="mt-12 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-2xl">Additional Tips for Safe Drinking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Track What You Drink</h3>
              <p className="text-gray-700">
                Keep count of how many standard drinks you're having. Use our calculator to understand what you're really consuming.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Avoid Drinking Games</h3>
              <p className="text-gray-700">
                Drinking games encourage rapid consumption and can lead to dangerous levels of intoxication.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Don't Mix with Medications</h3>
              <p className="text-gray-700">
                Many medications interact dangerously with alcohol. Check with your doctor or pharmacist if unsure.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Look Out for Friends</h3>
              <p className="text-gray-700">
                Stay together, check on each other, and never leave someone alone if they've had too much to drink.
              </p>
            </div>
            
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-gray-900 mb-2">Know When to Stop</h3>
              <p className="text-gray-700">
                If you're feeling tipsy, that's your body telling you to slow down or stop. Listen to it.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Remember Section */}
        <Card className="mt-12 bg-gradient-to-br from-teal-600 to-cyan-700 text-white">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Remember</h3>
            <p className="text-lg leading-relaxed">
              The best way to reduce the risks associated with drinking is to drink less. Every positive change you make matters, no matter how small. If you're concerned about your drinking or someone else's, help is available.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tips;